import React, { useState, useEffect, useMemo } from 'react';
import './CorporateDashboard.css';
import useTranslation from '../hooks/useTranslation';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import {
  corporateGetDashboardAnalytics,
  corporateListDepartments,
  corporateListStaffRoles,
  corporateCreateStaff,
  corporateCreateDepartment,
  corporateDeleteDepartment,
  corporateDeleteStaff,
  corporateGetStaffSessions,
} from '../components/api';
import {
  getBurnoutRiskLevel,
  getEmployeeBurnoutRisk,
} from '../utils/dashboardSharedUtils';
import EmployeeDetailModal from '../components/corporate/EmployeeDetailModal';
import DepartmentDetailModal from '../components/corporate/DepartmentDetailModal';
import SessionConductModal from '../components/corporate/SessionConductModal';
import EmployeeImportExport from '../components/corporate/EmployeeImportExport';

export default function CorporateDashboard({ userDetails }) {
  const { t } = useTranslation();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  // Removed showDepartmentModal - using showDepartmentReportModal instead
  const [employeeSessions, setEmployeeSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [filteredRiskLevel, setFilteredRiskLevel] = useState(null);
  const [showDepartmentReportModal, setShowDepartmentReportModal] = useState(false);
  const [deptViewMode, setDeptViewMode] = useState('overview');
  const [selectedReportDepartment, setSelectedReportDepartment] = useState(null);
  const [departmentSessions, setDepartmentSessions] = useState({});
  const [loadingDepartmentSessions, setLoadingDepartmentSessions] = useState(false);

  const [globalDepartmentFilter, setGlobalDepartmentFilter] = useState('');
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState('');
  const [selectedDepartmentFilter, setSelectedDepartmentFilter] = useState('');
  const [showAllDepartments, setShowAllDepartments] = useState(false);

  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [staffForm, setStaffForm] = useState({ name: '', email: '', department_id: '', role: '' });
  const [staffRoles, setStaffRoles] = useState([]);
  const [staffRolesLoading, setStaffRolesLoading] = useState(false);
  const [roleIsOther, setRoleIsOther] = useState(false);
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [creatingDepartment, setCreatingDepartment] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: null, id: null, name: '' });
  const [deleting, setDeleting] = useState(false);

  // Session modal pre-selection state
  const [sessionPreSelectedDepartment, setSessionPreSelectedDepartment] = useState(null);
  const [sessionPreSelectedStaff, setSessionPreSelectedStaff] = useState(null);

  const userId = localStorage.getItem('neuro_user_id');

  // Load dashboard data from API
  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Fetch existing staff roles when Add Staff modal opens (for Role dropdown)
  useEffect(() => {
    if (!showAddStaffModal) return;
    let cancelled = false;
    setStaffRolesLoading(true);
    (async () => {
      try {
        const res = await corporateListStaffRoles(userId);
        if (cancelled) return;
        // Support both { data: [...] } and direct array (e.g. axios response wrapper)
        const raw = res && (res.data !== undefined ? res.data : res);
        const list = Array.isArray(raw) ? raw : [];
        setStaffRoles(list.filter((r) => r != null && String(r).trim() !== ''));
      } catch (_) {
        if (!cancelled) setStaffRoles([]);
      } finally {
        if (!cancelled) setStaffRolesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showAddStaffModal, userId]);

  // Reset department report view when modal opens and load sessions
  useEffect(() => {
    if (showDepartmentReportModal && selectedReportDepartment) {
      setDeptViewMode('overview');
      loadDepartmentSessions(selectedReportDepartment.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDepartmentReportModal, selectedReportDepartment]);

  // Load sessions for all employees in a department
  const loadDepartmentSessions = async (deptId) => {
    if (!deptId || departmentSessions[deptId]) return; // Already loaded

    setLoadingDepartmentSessions(true);
    try {
      const deptEmployees = (employees || []).filter(emp => emp.department_id === deptId);
      const allSessions = [];

      // Fetch sessions for each employee in the department
      for (const emp of deptEmployees) {
        try {
          const res = await corporateGetStaffSessions(emp.id, 100); // Get up to 100 sessions per employee
          if (res.status_code === 200 && res.data) {
            const empSessions = (res.data || []).map(session => ({
              ...session,
              staff_id: emp.id,
              staff_name: emp.name
            }));
            allSessions.push(...empSessions);
          }
        } catch (err) {
          console.error(`Error loading sessions for employee ${emp.id}:`, err);
        }
      }

      setDepartmentSessions(prev => ({
        ...prev,
        [deptId]: allSessions
      }));
    } catch (err) {
      console.error('Error loading department sessions:', err);
    } finally {
      setLoadingDepartmentSessions(false);
    }
  };

  // Scroll to top whenever department view mode changes
  useEffect(() => {
    const modalContent = document.querySelector('.department-report-modal .modal-content');
    if (modalContent) {
      modalContent.scrollTop = 0;
    }
  }, [deptViewMode]);

  const loadDashboardData = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await corporateGetDashboardAnalytics(userId);
      if (res.status_code === 200 && res.data) {
        // Map API response to dashboard format
        const mappedStaff = (res.data.staff || []).map(staff => ({
          id: staff.staff_id,
          name: staff.name,
          department: staff.department_name || 'Unassigned',
          department_id: staff.department_id,
          email: staff.email,
          emotionScore: staff.emotion_score ?? null, // Use null instead of 0 to indicate no data
          burnoutFlag: staff.burnout_flag || false,
          motivationScore: staff.motivation_score ?? null,
          finalRiskScore: calculateFinalRiskScore(staff),
          engagementScore: staff.engagement_score ?? null,
          stressScore: staff.stress_score ?? null,
          wellbeingScore: staff.wellbeing_score ?? null,
          session_date: staff.session_date,
          has_data: staff.has_data !== false, // Preserve has_data flag from backend
          emotionTimeline: [], // Will be populated from session history if needed
          flagHistory: [], // Will be populated from session data if needed
          coachingSuggestions: [], // Will be generated based on scores if needed
          behavioralFlags: [] // Will be populated from session data if needed
        }));

        const mappedDepartments = (res.data.departments || []).map(dept => ({
          id: dept.department_id,
          name: dept.name,
          avgEmotionScore: dept.avg_emotion_score ?? null, // Use null instead of 0 to indicate no data
          burnoutPercentage: dept.burnout_percentage ?? null,
          employeeCount: dept.employee_count || 0,
          avgMotivationScore: dept.avg_motivation_score ?? null,
          avgEngagementScore: dept.avg_engagement_score ?? null,
          avgStressScore: dept.avg_stress_score ?? null,
          avgWellbeingScore: dept.avg_wellbeing_score ?? null,
          has_data: dept.has_data !== false, // Preserve has_data flag from backend
          trend: calculateTrend(dept),
          trendSlope: null, // Will be calculated from historical session data when available
          emotionCloud: [] // Will be generated from department session data when available
        }));

        // Bug 4 Fix: Detect if department averages leaked into individual scores
        // If all employees in a department have identical emotion/motivation scores,
        // it's likely the department average - set to null (show N/A instead)
        const deptGroups = {};
        mappedStaff.forEach(emp => {
          const dept = emp.department;
          if (!deptGroups[dept]) deptGroups[dept] = [];
          deptGroups[dept].push(emp);
        });
        Object.values(deptGroups).forEach(group => {
          if (group.length <= 1) return;
          const emotionScores = group.map(e => e.emotionScore).filter(s => s !== null);
          const motivationScores = group.map(e => e.motivationScore).filter(s => s !== null);
          // If all scores are identical across employees, they're department averages
          if (emotionScores.length > 1 && new Set(emotionScores).size === 1) {
            group.forEach(emp => { emp.emotionScore = null; emp.has_data = false; });
          }
          if (motivationScores.length > 1 && new Set(motivationScores).size === 1) {
            group.forEach(emp => { emp.motivationScore = null; });
          }
        });

        // Bug 8 Fix: Recalculate department averages from employees with actual data
        // Exclude employees without emotion exams from both averages and counts
        mappedDepartments.forEach(dept => {
          const deptEmployees = mappedStaff.filter(emp => emp.department === dept.name);
          // Only count employees with actual session data (has_data === true and emotionScore !== null)
          const employeesWithData = deptEmployees.filter(emp =>
            emp.has_data !== false &&
            emp.emotionScore !== null && emp.emotionScore !== undefined
          );

          // Update employee count to reflect only employees with data
          dept.employeeCount = employeesWithData.length;

          if (employeesWithData.length === 0) {
            dept.avgEmotionScore = null;
            dept.avgMotivationScore = null;
            dept.avgEngagementScore = null;
            dept.avgStressScore = null;
            dept.avgWellbeingScore = null;
            dept.burnoutPercentage = null;
            dept.has_data = false;
          } else {
            dept.avgEmotionScore = employeesWithData.reduce((sum, e) => sum + (e.emotionScore || 0), 0) / employeesWithData.length;
            dept.avgMotivationScore = employeesWithData.reduce((sum, e) => sum + (e.motivationScore || 0), 0) / employeesWithData.length;
            dept.avgEngagementScore = employeesWithData.reduce((sum, e) => sum + (e.engagementScore || 0), 0) / employeesWithData.length;
            dept.avgStressScore = employeesWithData.reduce((sum, e) => sum + (e.stressScore || 0), 0) / employeesWithData.length;
            dept.avgWellbeingScore = employeesWithData.reduce((sum, e) => sum + (e.wellbeingScore || 0), 0) / employeesWithData.length;
            const burnoutCount = employeesWithData.filter(e => e.burnoutFlag).length;
            dept.burnoutPercentage = (burnoutCount / employeesWithData.length) * 100;
            dept.has_data = true;
          }
        });

        setEmployees(mappedStaff);
        setDepartments(mappedDepartments);
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const calculateFinalRiskScore = (staff) => {
    // Only calculate risk score if staff has actual session data
    if (!staff.has_data || staff.emotion_score === null || staff.emotion_score === undefined) {
      return null; // Return null to indicate no data
    }
    // Calculate risk score: higher emotion_score, burnout_score, or lower motivation = higher risk
    const emotionRisk = (staff.emotion_score || 0) * 0.4;
    const burnoutRisk = (staff.burnout_score || 0) * 0.4;
    const motivationRisk = (100 - (staff.motivation_score || 0)) * 0.2;
    return Math.round(emotionRisk + burnoutRisk + motivationRisk);
  };

  const calculateTrend = (dept) => {
    // Simple trend calculation - can be enhanced with historical data
    if (dept.avg_emotion_score > 70) return 'negative';
    if (dept.avg_emotion_score < 40) return 'positive';
    return 'stable';
  };

  // Calculate session activity for a department
  const calculateSessionActivity = (deptId) => {
    const deptEmployees = (employees || []).filter(emp => emp.department_id === deptId);
    if (deptEmployees.length === 0) return { sessionsThisWeek: 0, participation: 0 };

    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    const employeesWithSessions = deptEmployees.filter(emp => {
      if (!emp.session_date) return false;
      const sessionDate = new Date(emp.session_date);
      return sessionDate >= oneWeekAgo;
    });

    const sessionsThisWeek = employeesWithSessions.length;
    const participation = deptEmployees.length > 0
      ? Math.round((sessionsThisWeek / deptEmployees.length) * 100)
      : 0;

    return { sessionsThisWeek, participation };
  };

  // Get top emotion for a department
  const getTopEmotion = (dept) => {
    // Check if department has actual session data
    const hasData = dept.has_data !== false && dept.avgEmotionScore !== null && dept.avgEmotionScore !== undefined;
    if (!hasData) {
      return { label: 'No Data', icon: '📊', color: '#6b7280' };
    }
    const emotion = dept.avgEmotionScore || 0;
    if (emotion >= 60) return { label: 'Positive', icon: '😊', color: '#10b981' };
    if (emotion >= 40) return { label: 'Neutral', icon: '😐', color: '#6b7280' };
    return { label: 'Negative', icon: '😔', color: '#ef4444' };
  };

  // getBurnoutRiskLevel and getEmployeeBurnoutRisk are imported from dashboardSharedUtils

  // Mock data removed - using real API data from loadDashboardData()

  // Departments loaded from API - see loadDashboardData()

  // Calculate risk levels based on backend logic
  const getRiskLevel = (score) => {
    // If score is null/undefined, return "no data" level
    if (score === null || score === undefined) {
      return { level: 'no-data', color: '#6b7280', icon: '📊' };
    }
    if (score < 40) return { level: 'low', color: '#16a34a', icon: '🟢' };
    if (score < 70) return { level: 'moderate', color: '#d97706', icon: '🟠' };
    return { level: 'high', color: '#dc2626', icon: '🔴' };
  };

  const getTrendArrow = (slope) => {
    if (slope > 5) return '📈';
    if (slope < -5) return '📉';
    return '➖';
  };

  // Filtering logic - must be defined before calculateWeeklyTrends
  const filteredEmployees = (employees || []).filter(emp => {
    if (globalDepartmentFilter && emp.department.toLowerCase() !== globalDepartmentFilter.toLowerCase()) {
      return false;
    }
    // Use burnout risk for filtering instead of emotion score
    if (filteredRiskLevel && getEmployeeBurnoutRisk(emp).level !== filteredRiskLevel) {
      return false;
    }
    return true;
  });

  // Calculate weekly trends dynamically from actual session data
  // Only shows current week data - no mock/estimated historical data
  const calculateWeeklyTrends = useMemo(() => {
    if (!employees || employees.length === 0) {
      return {
        avgEmotionScore: [],
        burnoutFlagCount: [],
        motivationScore: [],
        avgEngagementScore: [],
        avgStressScore: [],
        labels: []
      };
    }

    // Only use employees with actual session data for calculations
    const employeesWithData = filteredEmployees.filter(e =>
      e.has_data !== false &&
      (e.emotionScore !== null && e.emotionScore !== undefined) &&
      (e.motivationScore !== null && e.motivationScore !== undefined)
    );

    if (employeesWithData.length === 0) {
      return {
        avgEmotionScore: [],
        burnoutFlagCount: [],
        motivationScore: [],
        avgEngagementScore: [],
        avgStressScore: [],
        labels: []
      };
    }

    // Calculate current week metrics from employees with data
    const currentWeek = {
      avgEmotionScore: employeesWithData.reduce((sum, e) => sum + (e.emotionScore || 0), 0) / employeesWithData.length,
      burnoutFlagCount: employeesWithData.filter(e => e.burnoutFlag).length,
      motivationScore: employeesWithData.reduce((sum, e) => sum + (e.motivationScore || 0), 0) / employeesWithData.length,
      avgEngagementScore: employeesWithData.reduce((sum, e) => sum + (e.engagementScore || 0), 0) / employeesWithData.length,
      avgStressScore: employeesWithData.reduce((sum, e) => sum + (e.stressScore || 0), 0) / employeesWithData.length
    };

    // Return only current week data - no mock historical estimates
    // When historical session data is available, it will be calculated from actual sessions
    return {
      avgEmotionScore: [currentWeek.avgEmotionScore],
      burnoutFlagCount: [currentWeek.burnoutFlagCount],
      motivationScore: [currentWeek.motivationScore],
      avgEngagementScore: [currentWeek.avgEngagementScore],
      avgStressScore: [currentWeek.avgStressScore],
      labels: ['Current Week']
    };
  }, [employees, filteredEmployees]);

  const weeklyTrends = calculateWeeklyTrends;

  const filteredDepartments = (departments || []).filter(dept => {
    if (globalDepartmentFilter && dept.name.toLowerCase() !== globalDepartmentFilter.toLowerCase()) {
      return false;
    }
    return true;
  });

  // Get unique departments from departments state
  const availableDepartments = (departments || []).map(dept => dept.name);

  // Get visible employees (first 3-4 for display)
  const visibleEmployees = filteredEmployees.slice(0, 4);
  const visibleDepartments = filteredDepartments.slice(0, 3);

  // Red zone employees: high risk employees with actual session data
  const redZoneEmployees = (employees || []).filter(emp =>
    emp.has_data !== false &&
    (emp.finalRiskScore !== null && emp.finalRiskScore !== undefined) &&
    emp.finalRiskScore > 80
  );

  const handleEmployeeClick = async (employee) => {
    setSelectedEmployee(employee);
    setShowEmployeeModal(true);
    setEmployeeSessions([]);
    setLoadingSessions(true);
    try {
      const staffId = employee.id || employee.staff_id;
      if (staffId) {
        const res = await corporateGetStaffSessions(staffId, 50);
        if (res.status_code === 200 && res.data) {
          let sessions = res.data || [];

          // Sort sessions by date descending (newest first)
          sessions = sessions.sort((a, b) => {
            const dateA = a.session_date ? new Date(a.session_date).getTime() : 0;
            const dateB = b.session_date ? new Date(b.session_date).getTime() : 0;
            return dateB - dateA; // Descending order
          });

          setEmployeeSessions(sessions);
        } else {
          setEmployeeSessions([]);
        }
      } else {
        setEmployeeSessions([]);
      }
    } catch (err) {
      console.error('Error loading employee sessions:', err);
      setEmployeeSessions([]);
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleDepartmentClick = (department) => {
    // Open department report modal instead of old department modal
    setSelectedReportDepartment(department);
    setDeptViewMode('overview');
    setShowDepartmentReportModal(true);
  };

  const handleRiskFilter = (level) => {
    setFilteredRiskLevel(level === filteredRiskLevel ? null : level);
  };

  const handleGlobalDepartmentFilter = (deptName) => {
    setGlobalDepartmentFilter(deptName);
    setSelectedEmployeeFilter('');
    setSelectedDepartmentFilter('');
  };

  const handleEmployeeFilter = (employeeId) => {
    setSelectedEmployeeFilter(employeeId || '');
    if (employeeId) {
      const employee = employees.find(emp => emp.id === employeeId);
      if (employee) {
        handleEmployeeClick(employee);
      }
    }
  };

  // Load departments when Add Staff modal opens
  useEffect(() => {
    if (showAddStaffModal) {
      loadDepartments();
    }
  }, [showAddStaffModal]);

  const loadDepartments = async () => {
    try {
      const res = await corporateListDepartments(userId);
      if (res.status_code === 200 && res.data) {
        const mappedDepartments = res.data.map(dept => ({
          id: dept.department_id,
          name: dept.name,
          staff_count: dept.staff_count ?? 0,
          employeeCount: dept.staff_count ?? dept.employee_count ?? 0
        }));
        // Force update by setting state
        setDepartments(mappedDepartments);
        return mappedDepartments;
      }
      return [];
    } catch (err) {
      console.error('Error loading departments:', err);
      return [];
    }
  };

  // Add Department functionality
  const handleAddDepartment = async (e) => {
    e.preventDefault();
    if (!newDepartmentName.trim()) {
      alert('Please enter a department name');
      return;
    }
    setCreatingDepartment(true);
    try {
      const res = await corporateCreateDepartment(newDepartmentName.trim(), userId);
      if (res.status_code === 200) {
        const newDeptId = res.data.department_id;
        const newDeptName = newDepartmentName.trim();

        // Close modal first
        setNewDepartmentName('');
        setShowAddDepartmentModal(false);

        // Immediately add the new department to the state to ensure it shows in dropdown
        const newDept = {
          id: newDeptId,
          name: newDeptName,
          staff_count: 0
        };
        setDepartments(prev => {
          // Check if department already exists to avoid duplicates
          const exists = prev.some(d => d.id === newDeptId);
          if (exists) {
            return prev;
          }
          return [...prev, newDept];
        });

        // Refresh the departments list from API to ensure we have the latest
        await loadDepartments();

        // Refresh dashboard data (this will update departments with analytics)
        await loadDashboardData();

        // Refresh departments again after dashboard data loads
        await loadDepartments();

        // If we're in the Add Staff modal, select the newly created department
        if (showAddStaffModal) {
          // Use setTimeout to ensure state has updated
          setTimeout(() => {
            setStaffForm(prev => ({ ...prev, department_id: newDeptId }));
            setShowAddDepartment(false);
          }, 200);
        }

        alert('Department created successfully!');
      } else {
        alert(res.message || 'Failed to create department');
      }
    } catch (err) {
      console.error('Error creating department:', err);
      const status = err.response?.status;
      const data = err.response?.data;
      let msg = err.message || 'Unknown error';
      if (status === 502 || status === 503 || status === 504) {
        msg = 'Backend or proxy timed out. Ensure the backend container is running (e.g. port 6000).';
      } else if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') {
        msg = 'Cannot reach the server. Ensure the backend is running and the dev proxy target is correct (e.g. http://localhost:6000).';
      } else if (data?.detail) {
        msg = Array.isArray(data.detail) ? data.detail.map(d => d.msg || d).join(', ') : String(data.detail);
      } else if (data?.message) {
        msg = data.message;
      }
      alert('Error creating department: ' + msg);
    } finally {
      setCreatingDepartment(false);
    }
  };

  // Add Staff functionality
  const handleAddStaff = async (e) => {
    e.preventDefault();
    if (!staffForm.name || !staffForm.department_id) {
      alert('Please fill in all required fields');
      return;
    }
    try {
      const res = await corporateCreateStaff(staffForm, userId);
      if (res.status_code === 200) {
        alert('Staff member added successfully!');
        setShowAddStaffModal(false);
        setStaffForm({ name: '', email: '', department_id: '', role: '' });
        setRoleIsOther(false);
        setShowAddDepartment(false);
        setNewDepartmentName('');
        await loadDashboardData(); // Refetch so new staff appears in Conduct Session
      } else {
        alert(res.message || 'Failed to add staff member');
      }
    } catch (err) {
      console.error('Error adding staff:', err);
      alert('Error adding staff member: ' + (err.message || 'Unknown error'));
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm.show || !deleteConfirm.id) return;
    setDeleting(true);
    try {
      if (deleteConfirm.type === 'department') {
        const res = await corporateDeleteDepartment(deleteConfirm.id);
        if (res.status_code === 200) {
          setDeleteConfirm({ show: false, type: null, id: null, name: '' });
          await loadDashboardData();
          await loadDepartments();
        } else {
          alert(res.message || 'Failed to delete department');
        }
      } else if (deleteConfirm.type === 'staff') {
        const res = await corporateDeleteStaff(deleteConfirm.id);
        if (res.status_code === 200) {
          setDeleteConfirm({ show: false, type: null, id: null, name: '' });
          setShowEmployeeModal(false);
          setSelectedEmployee(null);
          await loadDashboardData();
          await loadDepartments();
        } else {
          alert(res.message || 'Failed to delete staff member');
        }
      }
    } catch (err) {
      console.error('Error deleting:', err);
      alert('Error: ' + (err.message || 'Unknown error'));
    } finally {
      setDeleting(false);
    }
  };

  const handleDepartmentFilter = (deptId) => {
    setSelectedDepartmentFilter(deptId);
    if (deptId) {
      const department = departments.find(dept => dept.id === deptId);
      if (department) {
        handleDepartmentClick(department);
      }
    }
  };

  // Tooltip state
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });

  const handleSliceMouseEnter = (e, risk, percent, count) => {
    const rect = e.target.getBoundingClientRect();
    setTooltip({
      show: true,
      content: `${risk} Risk: ${percent}% (${count} employees)`,
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
  };

  const handleSliceMouseLeave = () => {
    setTooltip({ show: false, content: '', x: 0, y: 0 });
  };

  return (
    <div className="corporate-dashboard">
      {/* Header */}
      <header className="corporate-header">
        <h1 className="corporate-title">{t('Corporate Well-being Dashboard')}</h1>
        <button
          className="conduct-session-btn"
          onClick={() => {
            setSessionPreSelectedDepartment(null);
            setSessionPreSelectedStaff(null);
            setShowSessionModal(true);
          }}
        >
          🎤 {t('Conduct Session')}
        </button>
        <div className="header-actions">
          <button className="download-btn" onClick={() => setShowAddDepartmentModal(true)}>
            ➕ {t('Add Dept')}
          </button>
          <button className="download-btn" onClick={() => setShowAddStaffModal(true)}>
            ➕ {t('Add Staff')}
          </button>
          <EmployeeImportExport employees={employees} t={t} />
        </div>
      </header>

      <div className="corporate-content">
        {/* Global Department Filter */}
        <section className="dashboard-section">
          <div className="filter-section">
            <label htmlFor="global-department-filter" className="filter-label">
              {t('Filter by Department')}
            </label>
            <select
              id="global-department-filter"
              className="filter-dropdown"
              value={globalDepartmentFilter}
              onChange={(e) => handleGlobalDepartmentFilter(e.target.value)}
            >
              <option value="">{t('All Departments')}</option>
              {availableDepartments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
        </section>

        {/* Employee Mood Tracking */}
        <section className="dashboard-section">
          <h2 className="section-title">🎯 {t('Employee Mood Tracking')}</h2>

          {/* Employee Cards Grid */}
          <div className="employee-grid">
            {visibleEmployees.map(employee => {
              const riskLevel = getRiskLevel(employee.emotionScore);
              return (
                <div
                  key={employee.id}
                  className="employee-card"
                  onClick={() => handleEmployeeClick(employee)}
                >
                  <button
                    type="button"
                    className="employee-card-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirm({ show: true, type: 'staff', id: employee.id, name: employee.name });
                    }}
                    title={t('Delete staff member')}
                  >
                    🗑️
                  </button>
                  <div className="employee-avatar">
                    <div className="avatar-placeholder">{employee.name.charAt(0)}</div>
                    <div className={`risk-badge ${riskLevel.level}`}>
                      {riskLevel.icon}
                    </div>
                  </div>
                  <div className="employee-info">
                    <h3 className="employee-name">{employee.name}</h3>
                    <p className="employee-department">{employee.department}</p>
                    <div className="employee-scores">
                      <span className="score-item">
                        {t('Emotion:')} {employee.emotionScore !== null && employee.emotionScore !== undefined ? employee.emotionScore : 'N/A'}
                      </span>
                      <span className="score-item">
                        {t('Motivation:')} {employee.motivationScore !== null && employee.motivationScore !== undefined ? employee.motivationScore : 'N/A'}
                      </span>
                    </div>
                    {employee.burnoutFlag && (
                      <div className="burnout-flag">🔥 Burnout Risk</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Employee Dropdown Filter */}
          {filteredEmployees.length > 4 && (
            <div className="dropdown-filter-section">
              <label htmlFor="employee-filter" className="filter-label">
                Select Employee
              </label>
              <select
                id="employee-filter"
                className="filter-dropdown"
                value={selectedEmployeeFilter}
                onChange={(e) => handleEmployeeFilter(e.target.value)}
              >
                <option value="">Select an employee...</option>
                {filteredEmployees.map(employee => {
                  const riskLevel = getRiskLevel(employee.emotionScore);
                  return (
                    <option key={employee.id} value={employee.id}>
                      {employee.name} ({employee.department}) {employee.burnoutFlag ? '🔥' : ''} {riskLevel.icon}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {/* No Data Message */}
          {filteredEmployees.length === 0 && (
            <div className="no-data-message">
              <span>                    {t('No data available for this department.')}</span>
            </div>
          )}
        </section>

        {/* Department Selector */}
        <section className="dashboard-section">
          <h2 className="section-title">🌡 {t('Department Overview')}</h2>

          {/* Department Rows List */}
          <div className="department-row-list">
            {visibleDepartments.map(dept => {
              const sessionActivity = calculateSessionActivity(dept.id);
              const topEmotion = getTopEmotion(dept);
              const burnoutRisk = getBurnoutRiskLevel(dept.burnoutPercentage);

              return (
                <div
                  key={dept.id}
                  className={`department-row ${globalDepartmentFilter === dept.name ? 'selected' : ''}`}
                  onClick={() => handleDepartmentClick(dept)}
                >
                  {/* Left Section: Department Name, Employees, Top Emotion */}
                  <div className="dept-row-left">
                    <div className="dept-row-name-section">
                      <h3 className="dept-row-name">{dept.name}</h3>
                      <div className="dept-row-employee-count">
                        <span className="dept-icon-small">👥</span>
                        <span className="dept-count-text">{(dept.employeeCount ?? dept.staff_count ?? 0)} {t('employees')}</span>
                      </div>
                    </div>
                    <div className="dept-row-top-emotion">
                      <span className="top-emotion-icon" style={{ color: topEmotion.color }}>
                        {topEmotion.icon}
                      </span>
                      <span className="top-emotion-label" style={{ color: topEmotion.color }}>
                        {topEmotion.label}
                      </span>
                    </div>
                  </div>

                  {/* Center Section: Key Metrics */}
                  <div className="dept-row-center">
                    <div className="dept-row-metrics">
                      <div className="dept-row-metric-chip" title={t('Average Emotion')}>
                        <span className="metric-chip-icon">😊</span>
                        <span className="metric-chip-value">
                          {dept.has_data === false ? t('No completed emotion assessments') : dept.avgEmotionScore !== null && dept.avgEmotionScore !== undefined
                            ? dept.avgEmotionScore.toFixed(1)
                            : 'N/A'}
                        </span>
                      </div>
                      <div className="dept-row-metric-chip" title={t('Average Motivation')}>
                        <span className="metric-chip-icon">🚀</span>
                        <span className="metric-chip-value">
                          {dept.has_data === false ? t('No completed emotion assessments') : dept.avgMotivationScore !== null && dept.avgMotivationScore !== undefined
                            ? dept.avgMotivationScore.toFixed(1)
                            : 'N/A'}
                        </span>
                      </div>
                      <div className="dept-row-metric-chip" title={t('Burnout Risk')}>
                        <span className="metric-chip-icon">🔥</span>
                        <span className="metric-chip-value">
                          {dept.has_data === false ? t('No completed emotion assessments') : dept.burnoutPercentage !== null && dept.burnoutPercentage !== undefined
                            ? `${dept.burnoutPercentage.toFixed(1)}%`
                            : 'N/A'}
                        </span>
                        <span
                          className={`metric-chip-badge ${burnoutRisk.level}`}
                          style={{ backgroundColor: burnoutRisk.bgColor, color: burnoutRisk.color }}
                        >
                          {burnoutRisk.label}
                        </span>
                      </div>
                      <div className="dept-row-metric-chip" title={t('Average Engagement')}>
                        <span className="metric-chip-icon">💼</span>
                        <span className="metric-chip-value">
                          {dept.has_data === false ? t('No completed emotion assessments') : dept.avgEngagementScore !== null && dept.avgEngagementScore !== undefined
                            ? dept.avgEngagementScore.toFixed(1)
                            : 'N/A'}
                        </span>
                      </div>
                      <div className="dept-row-metric-chip" title={t('Average Stress')}>
                        <span className="metric-chip-icon">😰</span>
                        <span className="metric-chip-value">
                          {dept.has_data === false ? t('No completed emotion assessments') : dept.avgStressScore !== null && dept.avgStressScore !== undefined
                            ? dept.avgStressScore.toFixed(1)
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right Section: View Reports + Delete */}
                  <div className="dept-row-right">
                    <button
                      className="dept-row-view-reports-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedReportDepartment(dept);
                        setDeptViewMode('overview');
                        setShowDepartmentReportModal(true);
                      }}
                      title={t('View detailed reports and analytics for this department')}
                    >
                      <span className="view-reports-icon">📊</span>
                      <span className="view-reports-text">{t('View Reports')}</span>
                    </button>
                    <button
                      type="button"
                      className="dept-row-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm({ show: true, type: 'department', id: dept.id, name: dept.name });
                      }}
                      title={t('Delete department')}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Department Dropdown Filter */}
          {filteredDepartments.length > 3 && (
            <div className="dropdown-filter-section">
              <label htmlFor="department-filter" className="filter-label">
                Select Department
              </label>
              <select
                id="department-filter"
                className="filter-dropdown"
                value={selectedDepartmentFilter}
                onChange={(e) => handleDepartmentFilter(e.target.value)}
              >
                <option value="">Select a department...</option>
                {filteredDepartments.map(dept => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name} ({dept.employeeCount ?? dept.staff_count ?? 0} employees)
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Expandable Department List */}
          {filteredDepartments.length > 3 && (
            <div className="expandable-departments">
              <button
                className="expand-toggle-btn"
                onClick={() => setShowAllDepartments(!showAllDepartments)}
              >
                {showAllDepartments ? t('Hide') : t('Show')} {t('All Departments')} ({filteredDepartments.length})
              </button>

              {showAllDepartments && (
                <div className="all-departments-list">
                  {filteredDepartments.slice(3).map(dept => (
                    <div
                      key={dept.id}
                      className="department-list-item"
                      onClick={() => handleDepartmentClick(dept)}
                    >
                      <span className="dept-name">{dept.name}</span>
                      <span className="dept-stats">
                        {dept.avgEmotionScore} avg | {dept.burnoutPercentage}% risk | {dept.employeeCount ?? dept.staff_count ?? 0} employees
                      </span>
                      <span className="trend-indicator">{getTrendArrow(dept.trendSlope)}</span>
                      <button
                        type="button"
                        className="department-list-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm({ show: true, type: 'department', id: dept.id, name: dept.name });
                        }}
                    title={t('Delete department')}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {/* Burnout Risk Alerts */}
        <section className="dashboard-section compact">
          <h2 className="section-title">🔥 {t('Burnout Risk Alerts')}</h2>
          <div className="risk-alerts-content compact">
            <div className="risk-pie-chart compact">
              <div className="pie-chart-container compact">
                <div className="pie-chart compact">
                  {/* Compact donut chart */}
                  <svg width="150" height="150" viewBox="0 0 150 150" className="pie-svg compact">
                    <defs>
                      <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                        <feDropShadow dx="2" dy="2" stdDeviation="2" floodColor="rgba(0,0,0,0.1)" />
                      </filter>
                    </defs>

                    {/* Background circle (donut ring) - always visible */}
                    <circle cx="75" cy="75" r="45" fill="none" stroke="#e5e7eb" strokeWidth="15" />

                    {/* Calculate percentages based on burnout risk */}
                    {(() => {
                      // Only include employees with actual session data for burnout risk calculation
                      const employeesWithData = filteredEmployees.filter(emp =>
                        emp.has_data !== false &&
                        (emp.finalRiskScore !== null && emp.finalRiskScore !== undefined)
                      );

                      const lowCount = employeesWithData.filter(emp => getEmployeeBurnoutRisk(emp).level === 'low').length;
                      const moderateCount = employeesWithData.filter(emp => getEmployeeBurnoutRisk(emp).level === 'moderate').length;
                      const highCount = employeesWithData.filter(emp => getEmployeeBurnoutRisk(emp).level === 'high').length;
                      const total = employeesWithData.length;

                      // If no data, show placeholder
                      if (total === 0) {
                        return (
                          <>
                            <circle cx="75" cy="75" r="45" fill="none" stroke="#d1d5db" strokeWidth="30" strokeDasharray="5 5" opacity="0.5" />
                            <text x="75" y="75" textAnchor="middle" dominantBaseline="middle" fontSize="12" fill="#9ca3af" fontWeight="500">
                              No Data
                            </text>
                          </>
                        );
                      }

                      const lowPercent = Math.round((lowCount / total) * 100);
                      const moderatePercent = Math.round((moderateCount / total) * 100);
                      const highPercent = Math.round((highCount / total) * 100);

                      // Ensure percentages add up to 100 (handle rounding errors)
                      const totalPercent = lowPercent + moderatePercent + highPercent;
                      const diff = 100 - totalPercent;
                      let adjustedLow = lowPercent;
                      let adjustedModerate = moderatePercent;
                      let adjustedHigh = highPercent;

                      if (diff !== 0) {
                        if (lowCount > 0) adjustedLow += diff;
                        else if (moderateCount > 0) adjustedModerate += diff;
                        else if (highCount > 0) adjustedHigh += diff;
                      }

                      // Calculate angles for donut slices
                      const lowAngle = (adjustedLow / 100) * 360;
                      const moderateAngle = (adjustedModerate / 100) * 360;
                      const highAngle = (adjustedHigh / 100) * 360;

                      // Starting angles
                      let currentAngle = -90; // Start from top

                      return (
                        <>
                          {/* Low Risk Slice */}
                          {adjustedLow > 0 && (
                            <path
                              d={`M ${75 + 45 * Math.cos(currentAngle * Math.PI / 180)} ${75 + 45 * Math.sin(currentAngle * Math.PI / 180)} A 45 45 0 ${lowAngle > 180 ? 1 : 0} 1 ${75 + 45 * Math.cos((currentAngle + lowAngle) * Math.PI / 180)} ${75 + 45 * Math.sin((currentAngle + lowAngle) * Math.PI / 180)} L ${75 + 30 * Math.cos((currentAngle + lowAngle) * Math.PI / 180)} ${75 + 30 * Math.sin((currentAngle + lowAngle) * Math.PI / 180)} A 30 30 0 ${lowAngle > 180 ? 1 : 0} 0 ${75 + 30 * Math.cos(currentAngle * Math.PI / 180)} ${75 + 30 * Math.sin(currentAngle * Math.PI / 180)} Z`}
                              fill="#16a34a"
                              filter="url(#shadow)"
                              className="pie-slice"
                              data-risk="low"
                              data-percent={lowPercent}
                              data-count={lowCount}
                              onMouseEnter={(e) => handleSliceMouseEnter(e, 'Low', lowPercent, lowCount)}
                              onMouseLeave={handleSliceMouseLeave}
                            />
                          )}

                          {/* Moderate Risk Slice */}
                          {adjustedModerate > 0 && (
                            <path
                              d={`M ${75 + 45 * Math.cos((currentAngle + lowAngle) * Math.PI / 180)} ${75 + 45 * Math.sin((currentAngle + lowAngle) * Math.PI / 180)} A 45 45 0 ${moderateAngle > 180 ? 1 : 0} 1 ${75 + 45 * Math.cos((currentAngle + lowAngle + moderateAngle) * Math.PI / 180)} ${75 + 45 * Math.sin((currentAngle + lowAngle + moderateAngle) * Math.PI / 180)} L ${75 + 30 * Math.cos((currentAngle + lowAngle + moderateAngle) * Math.PI / 180)} ${75 + 30 * Math.sin((currentAngle + lowAngle + moderateAngle) * Math.PI / 180)} A 30 30 0 ${moderateAngle > 180 ? 1 : 0} 0 ${75 + 30 * Math.cos((currentAngle + lowAngle) * Math.PI / 180)} ${75 + 30 * Math.sin((currentAngle + lowAngle) * Math.PI / 180)} Z`}
                              fill="#d97706"
                              filter="url(#shadow)"
                              className="pie-slice"
                              data-risk="moderate"
                              data-percent={moderatePercent}
                              data-count={moderateCount}
                              onMouseEnter={(e) => handleSliceMouseEnter(e, 'Moderate', moderatePercent, moderateCount)}
                              onMouseLeave={handleSliceMouseLeave}
                            />
                          )}

                          {/* High Risk Slice */}
                          {adjustedHigh > 0 && (
                            <path
                              d={`M ${75 + 45 * Math.cos((currentAngle + lowAngle + moderateAngle) * Math.PI / 180)} ${75 + 45 * Math.sin((currentAngle + lowAngle + moderateAngle) * Math.PI / 180)} A 45 45 0 ${highAngle > 180 ? 1 : 0} 1 ${75 + 45 * Math.cos((currentAngle + lowAngle + moderateAngle + highAngle) * Math.PI / 180)} ${75 + 45 * Math.sin((currentAngle + lowAngle + moderateAngle + highAngle) * Math.PI / 180)} L ${75 + 30 * Math.cos((currentAngle + lowAngle + moderateAngle + highAngle) * Math.PI / 180)} ${75 + 30 * Math.sin((currentAngle + lowAngle + moderateAngle + highAngle) * Math.PI / 180)} A 30 30 0 ${highAngle > 180 ? 1 : 0} 0 ${75 + 30 * Math.cos((currentAngle + lowAngle + moderateAngle) * Math.PI / 180)} ${75 + 30 * Math.sin((currentAngle + lowAngle + moderateAngle) * Math.PI / 180)} Z`}
                              fill="#dc2626"
                              filter="url(#shadow)"
                              className="pie-slice"
                              data-risk="high"
                              data-percent={highPercent}
                              data-count={highCount}
                              onMouseEnter={(e) => handleSliceMouseEnter(e, 'High', highPercent, highCount)}
                              onMouseLeave={handleSliceMouseLeave}
                            />
                          )}
                        </>
                      );
                    })()}
                  </svg>
                </div>

                {/* Chart title below the chart */}
                <div className="chart-title">
                              <span className="title-text">{t('Burnout Risk')}</span>
                  <span className="title-count">
                    – {(() => {
                      const employeesWithData = filteredEmployees.filter(emp =>
                        emp.has_data !== false &&
                        (emp.finalRiskScore !== null && emp.finalRiskScore !== undefined)
                      );
                      return employeesWithData.length;
                    })()} {t('Employees')}
                  </span>
                </div>
              </div>

              {/* Horizontal Legend */}
              <div className="pie-legend horizontal">
                {(() => {
                  // Only include employees with actual session data for burnout risk calculation
                  const employeesWithData = filteredEmployees.filter(emp =>
                    emp.has_data !== false &&
                    (emp.finalRiskScore !== null && emp.finalRiskScore !== undefined)
                  );

                  const lowCount = employeesWithData.filter(emp => getEmployeeBurnoutRisk(emp).level === 'low').length;
                  const moderateCount = employeesWithData.filter(emp => getEmployeeBurnoutRisk(emp).level === 'moderate').length;
                  const highCount = employeesWithData.filter(emp => getEmployeeBurnoutRisk(emp).level === 'high').length;
                  const total = employeesWithData.length;

                  const lowPercent = total > 0 ? Math.round((lowCount / total) * 100) : 0;
                  const moderatePercent = total > 0 ? Math.round((moderateCount / total) * 100) : 0;
                  const highPercent = total > 0 ? Math.round((highCount / total) * 100) : 0;

                  return (
                    <>
                      <div className="legend-item compact" onClick={() => handleRiskFilter('low')}>
                        <span className="legend-color low"></span>
                        <span className="legend-text">{t('Low')}</span>
                        <span className="legend-percent">{lowPercent}%</span>
                      </div>
                      <div className="legend-item compact" onClick={() => handleRiskFilter('moderate')}>
                        <span className="legend-color moderate"></span>
                        <span className="legend-text">{t('Moderate')}</span>
                        <span className="legend-percent">{moderatePercent}%</span>
                      </div>
                      <div className="legend-item compact" onClick={() => handleRiskFilter('high')}>
                        <span className="legend-color high"></span>
                        <span className="legend-text">{t('High')}</span>
                        <span className="legend-percent">{highPercent}%</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="risk-summary compact">
              <h3>{t('Risk Distribution')}</h3>
              {(() => {
                // Only include employees with actual session data for burnout risk calculation
                const employeesWithData = filteredEmployees.filter(emp =>
                  emp.has_data !== false &&
                  (emp.finalRiskScore !== null && emp.finalRiskScore !== undefined)
                );

                if (employeesWithData.length === 0) {
                  return (
                    <div className="no-risk-data" style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>
                      <p>📊 {t('No session data available')}</p>
                      <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>{t('Conduct sessions to view burnout risk distribution')}</p>
                    </div>
                  );
                }

                const lowCount = employeesWithData.filter(emp => getEmployeeBurnoutRisk(emp).level === 'low').length;
                const moderateCount = employeesWithData.filter(emp => getEmployeeBurnoutRisk(emp).level === 'moderate').length;
                const highCount = employeesWithData.filter(emp => getEmployeeBurnoutRisk(emp).level === 'high').length;

                return (
                  <div className="risk-stats compact">
                    <div className="risk-stat compact">
                      <span className="risk-count">{lowCount}</span>
                      <span className="risk-label">{t('Low Risk')}</span>
                    </div>
                    <div className="risk-stat compact">
                      <span className="risk-count">{moderateCount}</span>
                      <span className="risk-label">{t('Moderate Risk')}</span>
                    </div>
                    <div className="risk-stat compact">
                      <span className="risk-count">{highCount}</span>
                      <span className="risk-label">{t('High Risk')}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </section>

        {/* Scam/Fraud Detection */}
        {redZoneEmployees.length > 0 && (
          <section className="dashboard-section">
            <h2 className="section-title">🚨 {t('Red Zone Employees')}</h2>
            <div className="red-zone-content">
              {redZoneEmployees.map(employee => (
                <div key={employee.id} className="red-zone-card">
                  <div className="red-zone-header">
                    <span className="employee-name">{employee.name}</span>
                    <span className="employee-department">{employee.department}</span>
                    <span className="risk-score">Risk: {employee.finalRiskScore}</span>
                  </div>
                  <div className="flag-reasons">
                    <h4>{t('Flag Reasons:')}</h4>
                    <ul>
                      {(employee.behavioralFlags || []).length > 0 ? (
                        (employee.behavioralFlags || []).map(flag => (
                          <li key={flag.type} className={`flag-item ${flag.severity}`}>
                            {flag.type.replace(/_/g, ' ')} ({flag.severity})
                          </li>
                        ))
                      ) : (
                        <li>{t('No behavioral flags recorded.')}</li>
                      )}
                    </ul>
                  </div>
                  <button
                    className="drill-down-btn"
                    onClick={() => handleEmployeeClick(employee)}
                  >
                    {t('View Details')}
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Career Motivation Tracker */}
        <section className="dashboard-section">
          <h2 className="section-title">🚀 {t('Career Motivation Tracker')}</h2>
          <div className="motivation-content">
            <div className="motivation-overview">
              <div className="motivation-score">
                <h3>{t('Overall Motivation Index')}</h3>
                <div className="score-display">
                  <span className="score-value">
                    {(() => {
                      const employeesWithData = filteredEmployees.filter(emp =>
                        emp.has_data !== false &&
                        (emp.motivationScore !== null && emp.motivationScore !== undefined)
                      );
                      if (employeesWithData.length === 0) return 'N/A';
                      return Math.round(employeesWithData.reduce((sum, emp) => sum + (emp.motivationScore || 0), 0) / employeesWithData.length);
                    })()}
                  </span>
                  <span className="score-max">{t('/ 100')}</span>
                </div>
                <div className="score-change">
                  {/* Trend comparison will be available when historical session data is loaded */}
                  {weeklyTrends.motivationScore.length >= 2 ? (
                    <span className={weeklyTrends.motivationScore[1] >= weeklyTrends.motivationScore[0] ? 'positive' : 'negative'}>
                      {weeklyTrends.motivationScore[1] >= weeklyTrends.motivationScore[0] ? '📈' : '📉'}
                      {Math.abs(weeklyTrends.motivationScore[1] - weeklyTrends.motivationScore[0]).toFixed(1)} pts
                    </span>
                  ) : (
                    <span className="neutral">📊 Current data</span>
                  )}
                </div>
              </div>
              <div className="motivation-chart">
                <h4>{t('Motivation Trend (Past 2 Weeks)')}</h4>
                {weeklyTrends.motivationScore.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={weeklyTrends.labels.map((label, idx) => ({
                      week: label,
                      motivation: Math.round(weeklyTrends.motivationScore[idx] || 0)
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="motivation"
                        stroke="#8b5cf6"
                        fill="#8b5cf6"
                        fillOpacity={0.6}
                        name="Motivation Score"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="chart-placeholder">No data available</div>
                )}
              </div>
            </div>
            <div className="low-motivation-employees">
              {(() => {
                // Only consider employees with actual session data
                const employeesWithData = filteredEmployees.filter(emp =>
                  emp.has_data !== false &&
                  (emp.motivationScore !== null && emp.motivationScore !== undefined)
                );
                const lowMotivation = employeesWithData.filter(emp => (emp.motivationScore || 0) < 40);

                return (
                  <>
                    <h4>{t('Low Motivation Employees')} ({lowMotivation.length})</h4>
                    {lowMotivation.length > 0 ? (
                      lowMotivation.map(emp => (
                        <div key={emp.id} className="low-motivation-item">
                          <span className="employee-name">{emp.name}</span>
                          <span className="motivation-score">{emp.motivationScore || 0}</span>
                          <button
                            className="engagement-btn"
                            onClick={() => handleEmployeeClick(emp)}
                          >
                            {t('View Suggestions')}
                          </button>
                        </div>
                      ))
                    ) : employeesWithData.length === 0 ? (
                      <div className="no-low-motivation">📊 {t('No session data available - Conduct sessions to view motivation levels')}</div>
                    ) : (
                      <div className="no-low-motivation">✅ {t('All employees have healthy motivation levels')}</div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </section>

        {/* Reports & Trends */}
        <section className="dashboard-section">
          <h2 className="section-title">📊 {t('Reports & Trends')}</h2>
          <div className="reports-content">
            <div className="weekly-trends">
              <h3>{t('Weekly Aggregates')}</h3>
              <div className="trend-charts">
                <div className="trend-chart">
                  <h4>{t('Average Emotion Score')}</h4>
                  {weeklyTrends.avgEmotionScore.length > 0 ? (
                    <ResponsiveContainer width="100%" height={150}>
                      <LineChart data={weeklyTrends.labels.map((label, idx) => ({
                        week: label,
                        emotion: Math.round(weeklyTrends.avgEmotionScore[idx] || 0)
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="week" />
                        <YAxis domain={[0, 100]} />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="emotion"
                          stroke="#8884d8"
                          strokeWidth={2}
                          name="Emotion Score"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="chart-placeholder">No data available</div>
                  )}
                </div>
                <div className="trend-chart">
                  <h4>{t('Burnout Flag Count')}</h4>
                  {weeklyTrends.burnoutFlagCount.length > 0 ? (
                    <ResponsiveContainer width="100%" height={150}>
                      <BarChart data={weeklyTrends.labels.map((label, idx) => ({
                        week: label,
                        burnout: weeklyTrends.burnoutFlagCount[idx] || 0
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="week" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="burnout" fill="#ef4444" name="Burnout Flags" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="chart-placeholder">No data available</div>
                  )}
                </div>
                <div className="trend-chart">
                  <h4>{t('Motivation Score Trend')}</h4>
                  {weeklyTrends.motivationScore.length > 0 ? (
                    <ResponsiveContainer width="100%" height={150}>
                      <LineChart data={weeklyTrends.labels.map((label, idx) => ({
                        week: label,
                        motivation: Math.round(weeklyTrends.motivationScore[idx] || 0)
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="week" />
                        <YAxis domain={[0, 100]} />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="motivation"
                          stroke="#10b981"
                          strokeWidth={2}
                          name="Motivation Score"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="chart-placeholder">No data available</div>
                  )}
                </div>
              </div>
            </div>
            <div className="intervention-suggestions">
              <h3>{t('Intervention Suggestions')}</h3>
              {(() => {
                // Only consider employees with actual session data (has_data flag or non-null scores)
                const employeesWithData = filteredEmployees.filter(emp =>
                  emp.has_data !== false &&
                  (emp.emotionScore !== null && emp.emotionScore !== undefined) &&
                  (emp.motivationScore !== null && emp.motivationScore !== undefined)
                );

                const hasIssues = employeesWithData.some(emp =>
                  (emp.emotionScore || 0) > 70 ||
                  (emp.motivationScore || 0) < 40 ||
                  emp.burnoutFlag
                );

                if (hasIssues) {
                  return (
                    <div className="suggestions-list">
                      {employeesWithData.filter(emp => (emp.emotionScore || 0) > 70).length > 0 && (
                        <div className="suggestion-item">
                          <span className="suggestion-icon">⚠️</span>
                          <span>⚠️ {t('High emotion scores detected')} ({employeesWithData.filter(emp => (emp.emotionScore || 0) > 70).length} employees) - {t('Schedule team wellness sessions')}</span>
                        </div>
                      )}
                      {employeesWithData.filter(emp => (emp.motivationScore || 0) < 40).length > 0 && (
                        <div className="suggestion-item">
                          <span className="suggestion-icon">💡</span>
                          <span>💡 {t('Low motivation scores')} ({employeesWithData.filter(emp => (emp.motivationScore || 0) < 40).length} employees) - {t('Consider recognition programs')}</span>
                        </div>
                      )}
                      {employeesWithData.filter(emp => emp.burnoutFlag).length > 0 && (
                        <div className="suggestion-item">
                          <span className="suggestion-icon">🔥</span>
                          <span>🔥 {t('Burnout risk detected')} ({employeesWithData.filter(emp => emp.burnoutFlag).length} employees) - {t('Review workload and provide support')}</span>
                        </div>
                      )}
                    </div>
                  );
                } else if (employeesWithData.length === 0) {
                  return (
                    <div className="no-suggestions">
                      <span>📊 {t('No session data available - Conduct sessions to generate intervention suggestions')}</span>
                    </div>
                  );
                } else {
                  return (
                    <div className="no-suggestions">
                      <span>✅ {t('No immediate interventions needed - All metrics are within healthy ranges')}</span>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
        </section>
      </div>

      {/* Tooltip */}
      {tooltip.show && (
        <div
          className="chart-tooltip"
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translateX(-50%)',
            zIndex: 1000
          }}
        >
          {tooltip.content}
        </div>
      )}

      {/* Employee Modal */}
      {showEmployeeModal && selectedEmployee && (
        <EmployeeDetailModal
          employee={selectedEmployee}
          sessions={employeeSessions}
          loadingSessions={loadingSessions}
          onClose={() => {
            setShowEmployeeModal(false);
            setSelectedEmployee(null);
            setEmployeeSessions([]);
            setSelectedEmployeeFilter('');
          }}
          onDelete={(emp) => setDeleteConfirm({ show: true, type: 'staff', id: emp.id, name: emp.name })}
          onConductSession={(emp) => {
            setShowEmployeeModal(false);
            setSelectedEmployee(null);
            setEmployeeSessions([]);
            const empDept = emp.department || emp.department_name;
            const foundDept = departments?.find(d => d.name === empDept || d.id === emp.department_id) || null;
            setSessionPreSelectedDepartment(foundDept);
            setSessionPreSelectedStaff(emp);
            setShowSessionModal(true);
          }}
          departments={departments}
          t={t}
        />
      )}

      {/* Department Report Modal */}
      {showDepartmentReportModal && selectedReportDepartment && (
        <DepartmentDetailModal
          department={selectedReportDepartment}
          employees={employees}
          departmentSessions={departmentSessions}
          loadingDepartmentSessions={loadingDepartmentSessions}
          deptViewMode={deptViewMode}
          onViewModeChange={setDeptViewMode}
          onClose={() => { setShowDepartmentReportModal(false); setDeptViewMode('overview'); }}
          onLoadSessions={loadDepartmentSessions}
          t={t}
        />
      )}

      {/* Add Department Modal */}
      {showAddDepartmentModal && (
        <div className="modal-overlay" onClick={() => {
          setShowAddDepartmentModal(false);
          setNewDepartmentName('');
        }}>
          <div className="employee-modal create-form-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('Add New Department')}</h2>
              <button className="modal-close" onClick={() => {
                setShowAddDepartmentModal(false);
                setNewDepartmentName('');
              }}>×</button>
            </div>
            <div className="modal-content">
              <form onSubmit={handleAddDepartment} className="modal-form">
                <div className="form-group">
                  <label>{t('Department Name *')}</label>
                  <input
                    type="text"
                    value={newDepartmentName}
                    onChange={(e) => setNewDepartmentName(e.target.value)}
                    placeholder={t('Enter department name')}
                    required
                    autoFocus
                  />
                </div>
                <div className="modal-divider" />
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => {
                      setShowAddDepartmentModal(false);
                      setNewDepartmentName('');
                    }}
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={creatingDepartment || !newDepartmentName.trim()}
                  >
                    {creatingDepartment ? t('Creating...') : t('Create Department')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Staff Modal */}
      {showAddStaffModal && (
        <div className="modal-overlay" onClick={() => {
          setShowAddStaffModal(false);
          setShowAddDepartment(false);
          setNewDepartmentName('');
        }}>
          <div className="employee-modal create-form-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('Add New Staff Member')}</h2>
              <button className="modal-close" onClick={() => {
                setShowAddStaffModal(false);
                setShowAddDepartment(false);
                setNewDepartmentName('');
              }}>×</button>
            </div>
            <div className="modal-content">
              <form onSubmit={handleAddStaff} className="modal-form">
                <div className="form-group">
                  <label>{t('Name *')}</label>
                  <input
                    type="text"
                    value={staffForm.name}
                    onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                    placeholder={t('Enter staff member name')}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('Email')}</label>
                  <input
                    type="email"
                    value={staffForm.email}
                    onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                    placeholder={t('Enter email address (optional)')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('Role')}</label>
                  <select
                    value={roleIsOther ? '__other__' : (staffForm.role || '')}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '__other__') {
                        setRoleIsOther(true);
                        setStaffForm((prev) => ({ ...prev, role: '' }));
                      } else {
                        setRoleIsOther(false);
                        setStaffForm((prev) => ({ ...prev, role: v }));
                      }
                    }}
                    disabled={staffRolesLoading}
                  >
                    <option value="">{t('Select role (optional)')}</option>
                    {staffRoles.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                    <option value="__other__">{t('Other (type below)')}</option>
                  </select>
                  {roleIsOther && (
                    <input
                      type="text"
                      value={staffForm.role || ''}
                      onChange={(e) => setStaffForm((prev) => ({ ...prev, role: e.target.value }))}
                      placeholder="Enter custom role"
                      autoFocus
                    />
                  )}
                </div>
                <div className="form-group">
                  <label>{t('Department *')}</label>
                  <div className="form-group-dept-row">
                    <select
                      value={staffForm.department_id}
                      onChange={(e) => setStaffForm({ ...staffForm, department_id: e.target.value })}
                      required
                      disabled={showAddDepartment}
                    >
                      <option value="">{t('Select Department')}</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="inline-add"
                      onClick={() => {
                        setShowAddDepartment(!showAddDepartment);
                        if (!showAddDepartment) {
                          setNewDepartmentName('');
                        }
                      }}
                    >
                      {showAddDepartment ? t('Cancel') : t('+ Add Dept')}
                    </button>
                  </div>
                  {showAddDepartment && (
                    <div className="department-add-section">
                      <form onSubmit={handleAddDepartment}>
                        <input
                          type="text"
                          value={newDepartmentName}
                          onChange={(e) => setNewDepartmentName(e.target.value)}
                          placeholder={t('Enter department name')}
                          required
                          autoFocus
                        />
                        <button
                          type="submit"
                          disabled={creatingDepartment || !newDepartmentName.trim()}
                        >
                          {creatingDepartment ? t('Creating...') : t('Create')}
                        </button>
                      </form>
                    </div>
                  )}
                </div>
                <div className="modal-divider" />
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => {
                      setShowAddStaffModal(false);
                      setShowAddDepartment(false);
                      setNewDepartmentName('');
                      setStaffForm({ name: '', email: '', department_id: '', role: '' });
                      setRoleIsOther(false);
                    }}
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={showAddDepartment}
                  >
                    {t('Add Staff')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="modal-overlay delete-confirm-overlay" onClick={() => !deleting && setDeleteConfirm({ show: false, type: null, id: null, name: '' })}>
          <div className="delete-confirm-modal" onClick={e => e.stopPropagation()}>
            <h3 className="delete-confirm-title">
              Delete {deleteConfirm.type === 'department' ? t('Department') : t('Staff Member')}?
            </h3>
            {deleteConfirm.name && (
              <p className="delete-confirm-name">{deleteConfirm.name}</p>
            )}
            <p className="delete-confirm-warning">
              <span className="delete-confirm-warning-icon" aria-hidden>⚠️</span>
              {t('This action cannot be undone.')}
            </p>
            <div className="delete-confirm-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setDeleteConfirm({ show: false, type: null, id: null, name: '' })}
                disabled={deleting}
              >
                {t('Cancel')}
              </button>
              <button
                type="button"
                className="btn-confirm-delete"
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting ? t('Deleting...') : t('Confirm Delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session Conducting Modal */}
      {showSessionModal && (
        <SessionConductModal
          departments={departments}
          employees={employees}
          onClose={() => setShowSessionModal(false)}
          onComplete={() => {
            setShowSessionModal(false);
            loadDashboardData();
          }}
          t={t}
          userId={userId}
          preSelectedDepartment={sessionPreSelectedDepartment}
          preSelectedStaff={sessionPreSelectedStaff}
        />
      )}

      {loading && (
        <div className="loading-overlay">
                        <div className="loading-spinner">{t('Loading...')}</div>
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
    </div>
  );
} 